import React from 'react'
// @src imports
import {
  useActions,
  useEffect,
  usePersistedUserData,
  useSelector,
  useState,
  useRoute,
  useFlagsWithUseQueries,
} from 'src/hooks'

import { Flex, Text } from '@sendoutcards/quantum-design-ui'

import {
  CollapsibleBanner,
  CollapsibleBannerProps,
} from 'src/saleor/components/collapsable_banner/CollapsableBanner'
import LeaderBoard from '../../../components/Leaderboard/LeaderBoard'
import { AffiliateCreationForm } from 'src/saleor/components/affiliate_creation_form/AffiliateCreationForm'
import { CardHistoryWidget } from 'src/dashboard/components/Widgets/History/CardHistoryWidget'
import { RemindersListWidget } from 'src/dashboard/components/Widgets/Reminders/RemindersListWidget'
import { Label } from 'src/graphql/generated/graphql'
import {
  useAccountQuery,
  useLeaderBoards,
  useMarketingContent,
} from 'src/react_query'
import { AFFILIATE_BRANDING } from 'src/app/constants'
import { GratitudeMovementWidget } from 'src/dashboard/components/Widgets/GratitudeMovement/GratitudeMovementWidget'
import { PromptingsCommunityWidget } from 'src/dashboard/components/Widgets/PromptingsCommunity/PromptingsCommunityWidget'
import { Portal } from 'src/portal/portal'
import ExclusiveOfferScreen from 'src/onboarding/components/ExclusiveOfferScreen'
import { ExclusiveOfferConfirmationScreen } from 'src/onboarding/components/ExclusiveOfferConfirmationScreen'
import { DashboardSplashScreen } from 'src/dashboard/containers/DashboardSplashScreen/DashboardSplashScreen'
import { GiftCardsWidget } from 'src/dashboard/components/Widgets/GiftCards/GiftCardsWidget'

type DashboardHomeViewProps = {
  toggleIsAccountDrawerOpen: () => void
}

const DashboardHomeView: React.FC<DashboardHomeViewProps> = ({
  toggleIsAccountDrawerOpen,
}) => {
  const windowWidth = useSelector(state => state.window.width)

  const actions = useActions()
  const {
    controlData: { shouldDisplayJoinConfirmation },
  } = usePersistedUserData()
  const { hasGiftCardWallet } = useFlagsWithUseQueries()

  const isLeaderboardMobile = windowWidth <= 1500
  const shouldColumnWrap = windowWidth <= 1260
  const shouldWelcomeWrap = windowWidth < 855 && windowWidth > 574
  const isTablet = windowWidth <= 854
  const isMobile = windowWidth <= 516
  const shouldTwoColumnLayout = shouldColumnWrap && !isTablet
  const shouldStretchColumns = !shouldColumnWrap

  const [shouldShowAffiliateForm, setShouldShowAffiliateForm] = useState(false)
  const [shouldShowAffiliateBanner, setShouldShowAffiliateBanner] = useState<
    boolean | undefined
  >()
  const [hasClosedExclusiveOffer, setHasClosedExclusiveOffer] = useState(false)
  const [shouldShowSplashScreen, setShouldShowSplashScreen] = useState(false)

  const { data: account } = useAccountQuery()
  const route = useRoute()

  useEffect(() => {
    if (account && shouldShowAffiliateBanner === undefined) {
      setShouldShowAffiliateBanner(
        account.labels.includes(Label.NeedsAffiliateAccInfo),
      )
    }
  }, [account, shouldShowAffiliateBanner])

  useEffect(() => {
    setShouldShowSplashScreen('shouldDisplayDashboardSplash' in route.args)
  }, [route.args])

  const { data: marketingContent } = useMarketingContent()

  const getBannerData = (): CollapsibleBannerProps | undefined => {
    if (!account || !marketingContent) return undefined

    const profileWarningTitle =
      marketingContent.affiliateProfileWarningTitle.content
    const profileWarningBody =
      marketingContent.affiliateProfileWarningBody.content
    const profileCompleteTitle =
      marketingContent.affiliateProfileCompleteTitle.content

    const bannerBodyStyles = {
      fontSize: '14px',
      color: '#6B7280',
    }

    return account.labels.includes(Label.NeedsAffiliateAccInfo)
      ? {
          type: 'warning',
          title: profileWarningTitle,
          bannerActions: {
            primaryAction: {
              title: `Complete ${AFFILIATE_BRANDING.capitalized} Profile`,
              onClick: () => {
                setShouldShowAffiliateForm(true)
                setShouldShowAffiliateBanner(false)
              },
            },
          },
          children: (
            <Text
              type="body"
              content={profileWarningBody}
              style={{ ...bannerBodyStyles }}
            />
          ),
        }
      : {
          type: 'success',
          title: profileCompleteTitle,
          onClose: () => setShouldShowAffiliateBanner(false),
        }
  }

  const bannerData = getBannerData()
  const { data: leaderBoards } = useLeaderBoards()
  const [currentLeaderBoard] = leaderBoards ?? [undefined]

  const shouldShowLeaderBoard =
    currentLeaderBoard && currentLeaderBoard.challenges.length > 0
  const LeaderBoardWrapper = () =>
    currentLeaderBoard ? <LeaderBoard {...currentLeaderBoard} /> : <></>

  const isGsa = !!account && account.type === 'GSA'
  const isSparse = !!account && !account.username
  const hasSentFirstFreeCard = !account?.isQualifiedForFirstFreeCard
  const showExclusiveOffer =
    !hasClosedExclusiveOffer &&
    !account?.isQualifiedForFirstFreeCard &&
    hasSentFirstFreeCard &&
    isSparse

  if (shouldDisplayJoinConfirmation) {
    return (
      <Portal>
        <div
          style={{
            position: 'fixed',
            top: 0,
            width: '100%',
            height: '100%',
            overflowY: 'auto',
            zIndex: 999,
            backgroundColor: 'white',
          }}
        >
          <ExclusiveOfferConfirmationScreen
            onConfirm={() => {
              actions.setShouldDisplayJoinConfirmation(undefined)
              actions.setUpsellSlug('WILDCARD')
              actions.setPlanToDisplay('189')
            }}
            onClose={() => {
              actions.setShouldDisplayJoinConfirmation(undefined)
              actions.openAccount({ shouldDisplayDashboardSplash: '' })
            }}
          />
        </div>
      </Portal>
    )
  }

  return (
    <Flex
      width="100%"
      columnGap="24px"
      rowGap="24px"
      flexDirection="column"
      style={{
        maxWidth: 1420,
        margin: '0 auto',
        padding: '0',
      }}
    >
      {shouldShowAffiliateBanner && bannerData && (
        <Flex width="100%">
          <CollapsibleBanner
            outset="0px"
            onClose={() => setShouldShowAffiliateBanner(false)}
            {...bannerData}
          />
        </Flex>
      )}
      {shouldShowAffiliateForm && (
        <AffiliateCreationForm
          onSuccess={() => {
            setShouldShowAffiliateForm(false)
            setShouldShowAffiliateBanner(true)
          }}
          onCancel={() => {
            setShouldShowAffiliateForm(false)
            setShouldShowAffiliateBanner(true)
          }}
        />
      )}
      <Flex
        css={{
          width: '100%',
          display: isTablet ? 'flex' : 'grid',
          flexDirection: isTablet ? 'column' : undefined,
          gap: isTablet ? '24px' : shouldTwoColumnLayout ? '24px' : '28px',
          gridTemplateColumns: isTablet
            ? undefined
            : shouldTwoColumnLayout
              ? 'minmax(0, 1fr) minmax(0, 1fr)'
              : '1fr 1fr 1fr',
          gridAutoRows: 'minmax(0, auto)',
          alignItems: shouldColumnWrap ? 'start' : 'stretch',
        }}
      >
        <Flex
          flexDirection="column"
          rowGap="24px"
          width="100%"
          css={{
            gridColumn: shouldTwoColumnLayout
              ? '1'
              : shouldColumnWrap
                ? '1'
                : '1 / span 1',
            gridRow: shouldTwoColumnLayout
              ? 'auto'
              : shouldColumnWrap
                ? 'auto'
                : '1 / span 2',
            minHeight: shouldColumnWrap ? undefined : 680,
            minWidth: 0,
            height: shouldStretchColumns ? '100%' : undefined,
          }}
        >
          <Flex
            width="100%"
            style={{
              flex: shouldStretchColumns ? 1 : undefined,
              minHeight: shouldStretchColumns ? 0 : undefined,
            }}
          >
            <PromptingsCommunityWidget />
          </Flex>
          {shouldTwoColumnLayout && <CardHistoryWidget />}
          {shouldColumnWrap && !shouldTwoColumnLayout && (
            <>
              <RemindersListWidget isLandscape={windowWidth >= 855} />
            </>
          )}
        </Flex>

        <Flex
          flexDirection="column"
          rowGap="24px"
          width="100%"
          css={{
            gridColumn: shouldTwoColumnLayout
              ? '2'
              : shouldColumnWrap
                ? '1'
                : '2 / span 1',
            gridRow: shouldTwoColumnLayout
              ? 'auto'
              : shouldColumnWrap
                ? 'auto'
                : '1 / span 2',
            minWidth: 0,
            height: shouldColumnWrap ? undefined : '100%',
          }}
        >
          <Flex
            width="100%"
            css={{ display: 'grid' }}
            style={{
              flex: shouldStretchColumns ? 1 : undefined,
              minHeight: shouldStretchColumns ? 0 : undefined,
              height: shouldStretchColumns ? '100%' : undefined,
            }}
          >
            <GratitudeMovementWidget
              isMobile={isMobile}
              isTablet={isTablet}
              title={
                isGsa
                  ? 'Gratitude Movement Presentation'
                  : 'Learn about the Gratitude Movement'
              }
            />
          </Flex>
          {!shouldTwoColumnLayout && (
            <Flex
              width="100%"
              style={{
                flex: shouldStretchColumns ? 1 : undefined,
                minHeight: shouldStretchColumns ? 0 : undefined,
              }}
            >
              <CardHistoryWidget fillHeight={shouldStretchColumns} />
            </Flex>
          )}
          {hasGiftCardWallet && <GiftCardsWidget />}
          {shouldTwoColumnLayout && (
            <RemindersListWidget isLandscape={shouldWelcomeWrap} />
          )}
          {shouldShowLeaderBoard && isLeaderboardMobile && (
            <Flex width="100%" css={{ display: 'grid', minHeight: '220px' }}>
              <LeaderBoardWrapper />
            </Flex>
          )}
        </Flex>

        <Flex
          flexDirection="column"
          rowGap="24px"
          css={{
            display: shouldColumnWrap ? 'none' : 'flex',
            gridColumn: '3 / span 1',
            gridRow: '1 / span 2',
            minWidth: 0,
            maxWidth: '420px',
            height: shouldStretchColumns ? '100%' : undefined,
          }}
        >
          {!shouldColumnWrap && (
            <>
              <RemindersListWidget />
            </>
          )}
        </Flex>
      </Flex>
      {showExclusiveOffer && (
        <Portal>
          <div
            style={{
              position: 'fixed',
              top: 0,
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              zIndex: 510,
              backgroundColor: 'white',
            }}
          >
            <ExclusiveOfferScreen
              onClose={() => setHasClosedExclusiveOffer(true)}
            />
          </div>
        </Portal>
      )}
      {shouldShowSplashScreen && (
        <DashboardSplashScreen
          onClose={() => setShouldShowSplashScreen(false)}
        />
      )}
    </Flex>
  )
}

export default DashboardHomeView
